import type { InboxMessage, MerchantPublicConfig, RewardSummary, WalletMembership } from "@moxipass/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

type Envelope<T> = { data: T; requestId: string };
type LoginResponse = { userId: string; actorType: string; expiresAt: string; sessionToken: string; csrfToken: string };

const CSRF_STORAGE_KEY = "moxipass:csrf";
const SESSION_STORAGE_KEY = "moxipass:session";

function csrfToken() {
  const stored = sessionStorage.getItem(CSRF_STORAGE_KEY);
  if (stored) return stored;
  return document.cookie
    .split("; ")
    .find((entry) => entry.startsWith("mp_csrf="))
    ?.split("=")[1];
}

function isUnsafeMethod(method: string | undefined) {
  return !["GET", "HEAD", "OPTIONS"].includes((method ?? "GET").toUpperCase());
}

async function request<T>(path: string, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  headers.set("content-type", "application/json");
  headers.set("x-actor-id", "usr_01jz0000000000000000000100");
  headers.set("x-actor-type", "customer");
  const sessionToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (sessionToken) {
    headers.set("authorization", `Bearer ${sessionToken}`);
  }
  const csrf = csrfToken();
  if (isUnsafeMethod(init?.method) && csrf) {
    headers.set("x-csrf-token", decodeURIComponent(csrf));
  }
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers
  });
  const payload = (await response.json()) as Envelope<T> | { error: { message: string } };
  if (!response.ok || "error" in payload) {
    throw new Error("error" in payload ? payload.error.message : `Request failed: ${response.status}`);
  }
  return payload.data;
}

async function loginRequest(email: string, turnstileToken?: string) {
  const result = await request<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, actorType: "customer", turnstileToken })
  });
  sessionStorage.setItem(CSRF_STORAGE_KEY, result.csrfToken);
  sessionStorage.setItem(SESSION_STORAGE_KEY, result.sessionToken);
  return result;
}

export const api = {
  session: () =>
    request<{ userId: string; actorType: string; role?: string; merchantId?: string; expiresAt?: string }>("/v1/auth/session"),
  login: loginRequest,
  logout: async () => {
    const result = await request<{ loggedOut: boolean }>("/v1/auth/logout", { method: "POST", body: JSON.stringify({}) });
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return result;
  },
  merchantBySlug: (slug: string) => request<MerchantPublicConfig>(`/v1/public/merchants/${slug}`),
  memberships: () => request<WalletMembership[]>("/v1/wallet/memberships"),
  rewards: (merchantId: string) => request<RewardSummary[]>(`/v1/wallet/merchants/${merchantId}/rewards`),
  inbox: () => request<InboxMessage[]>("/v1/wallet/inbox"),
  join: (merchantSlug: string, marketing: boolean, turnstileToken?: string) =>
    request<{ membershipId: string; merchantSlug: string; customerId: string; consentRecorded: boolean; membership: WalletMembership }>(
      "/v1/wallet/join",
      {
        method: "POST",
        body: JSON.stringify({ merchantSlug, marketing, turnstileToken })
      }
    ),
  redemptionIntent: (body: Record<string, unknown>) =>
    request<{ redemptionId: string; redemptionToken: string; expiresAt: string }>("/v1/wallet/redemptions", {
      method: "POST",
      body: JSON.stringify(body)
    })
};

export function idempotencyKey(scope: string) {
  return `${scope}:${crypto.randomUUID()}`;
}
